// Shared state/behaviour for every surface that sells conversions: the
// paywall dialog (SubscribeDialog) and the result-page offer (ResultOffer).
// Packs are one-time credit purchases over the shared /credits/purchase
// endpoint; plans are the monthly subscriptions. Counts are framed in
// typical High-realism conversions from config.CONVERSION_CREDITS.
import { apiClient } from "@shared/services/api";
import { isMember, registerFirst } from "@shared/utils/memberFunnel";
import { CREDIT_PACKS, FIRST_PACK_CREDITS, approxConversions } from "@/config";

export default {
  data() {
    return {
      plans: [],
      loading: false,
      loaded: false,
      packLoading: null,
      packError: null
    };
  },
  computed: {
    // The standard packs, with the one-time welcome pack in front while
    // the balance payload says this account hasn't purchased yet.
    packs() {
      const list = CREDIT_PACKS.map(credits => ({
        credits,
        photos: approxConversions(credits, "high")
      }));
      const balance = this.$store.getters.creditBalance;
      if (balance && balance.first_pack_available) {
        list.unshift({
          credits: FIRST_PACK_CREDITS,
          photos: approxConversions(FIRST_PACK_CREDITS, "high"),
          first: true
        });
      }
      return list;
    },
    // Whole hours left on the welcome-pack window (null when not applicable).
    firstPackHoursLeft() {
      const balance = this.$store.getters.creditBalance;
      const expires = balance && balance.first_pack_expires_at;
      if (!expires) return null;
      const left = Math.floor(
        (new Date(expires).getTime() - Date.now()) / 3600000
      );
      return left > 0 ? left : null;
    },
    paidPlans() {
      return this.plans
        .filter(p => Number(p.price_monthly) > 0)
        .sort((a, b) => Number(a.price_monthly) - Number(b.price_monthly));
    },
    isAnonymous() {
      const user = this.$store.getters.currentUser;
      return !user || !!user.is_anonymous;
    }
  },
  methods: {
    // Hook: where Stripe returns the buyer. null = the shared default
    // (/billing). Components override this.
    checkoutUrls() {
      return null;
    },
    // Hook: called before leaving the page for a plan or sign-up.
    onNavigate() {},
    // Guests register first (their guest account upgrades in place, keeping
    // the converted image), then land on /subscribe with the plan they
    // picked; registered users go straight to /subscribe.
    choosePlan(plan) {
      const planName = String(plan.name || "").toLowerCase();
      const target = isMember(this.$store)
        ? { path: "/subscribe", query: { plan: planName } }
        : registerFirst("/subscribe", planName);
      this.onNavigate();
      this.$router.push(target).catch(() => {});
    },
    // Must track the backend's top-up rates (CREDITS_PER_DOLLAR = 10 for
    // subscribers, FREE_TOPUP_CREDITS_PER_DOLLAR = 7 otherwise).
    packRate() {
      const user = this.$store.getters.currentUser;
      const plan = String(
        (user && user.subscription_plan) || "free"
      ).toLowerCase();
      return plan === "free" || plan === "none" || plan === "" ? 7 : 10;
    },
    packPrice(credits) {
      return (credits / this.packRate()).toFixed(2);
    },
    async buyPack(pack) {
      if (this.packLoading !== null) return;
      this.packLoading = pack.credits;
      this.packError = null;
      try {
        const urls = this.checkoutUrls();
        const payload = urls
          ? {
              amount: pack.credits,
              successUrl: urls.successUrl,
              cancelUrl: urls.cancelUrl
            }
          : pack.credits;
        const response = await this.$store.dispatch("purchaseCredits", payload);
        if (response && response.checkout_url) {
          window.location.href = response.checkout_url;
          return;
        }
        this.packError = "Couldn't start the checkout. Please try again.";
      } catch (e) {
        const detail = e.response && e.response.data && e.response.data.detail;
        this.packError =
          (detail && (detail.message || detail)) ||
          "Couldn't start the checkout. Please try again.";
      } finally {
        this.packLoading = null;
      }
    },
    async fetchPlans() {
      this.loading = true;
      try {
        const response = await apiClient.get("/subscriptions/plans", {
          params: { is_active: true }
        });
        this.plans = response.data || [];
        this.loaded = true;
      } catch (e) {
        this.plans = [];
      } finally {
        this.loading = false;
      }
    },
    planTitle(plan) {
      const name = String(plan.name || "");
      return name.charAt(0).toUpperCase() + name.slice(1);
    },
    isFeatured(plan) {
      // Pro is the anchor offer regardless of how many tiers exist.
      const names = this.paidPlans.map(p => String(p.name || "").toLowerCase());
      if (names.indexOf("pro") !== -1) {
        return String(plan.name || "").toLowerCase() === "pro";
      }
      return this.paidPlans.indexOf(plan) === 1;
    },
    highCount(plan) {
      return approxConversions(Number(plan.credits_per_month || 0), "high");
    },
    basicCount(plan) {
      return approxConversions(Number(plan.credits_per_month || 0), "basic");
    },
    extremeCount(plan) {
      return approxConversions(Number(plan.credits_per_month || 0), "extreme");
    }
  }
};
